/**
 * @module mediaTypeUtils
 */

/**
 * Convenience function to convert the specified media type to path-ready value.
 *
 * @param {string} type - The media type value (e.g. 'episode').
 *
 * @returns {string} The converted type to path-ready with forward slash, pipe-delimited string (e.g. '/Konnect/|/Crosstown/|/Loop/|/Early-Childhood/|/Stories/').
 */
function mediaTypeToPath(type) {
  let path = '';
  const episodic = [
    'Konnect',
    'Crosstown',
    'Loop',
    'Early-Childhood',
    'Stories',
  ];
  const podcast = ['Craig-Groeschel-Leadership-Podcast'];
  const message = ['Weekend-Messages'];
  const song = ['Worship'];
  switch (type) {
    case 'episode':
      episodic.forEach((s, i) => {
        path += `/${s}/`;
        /* istanbul ignore next */
        if (i < episodic.length - 1) {
          path += `|`;
        }
      });
      break;
    case 'podcast':
      podcast.forEach((s, i) => {
        path += `/${s}/`;
        /* istanbul ignore next */
        if (i < podcast.length - 1) {
          path += `|`;
        }
      });
      break;
    case 'message':
      message.forEach((s, i) => {
        path += `/${s}/`;
        /* istanbul ignore next */
        if (i < message.length - 1) {
          path += `|`;
        }
      });
      break;
    case 'song':
      song.forEach((s, i) => {
        path += `/${s}/`;
        /* istanbul ignore next */
        if (i < song.length - 1) {
          path += `|`;
        }
      });
      break;
    default:
      path = type;
      break;
  }
  return path;
}

/**
 * Note: The ignore directive is because this is called conditionally from the
 * main `mediaUrlFromType` function only when `type` is a string, thus it will
 * never come in to this method `null`, `undefined`, or a blank string. This
 * results in it being seen as not fully covered.
 */

/**
 * Convenience function to generate a media url from the specified media type.
 *
 * @param {string} type - The media type value (e.g. 'messages').
 * @param {string} subCollection - Optional sub-collection value to add to the final url.
 * @param {string} itemSlug - Optional item slug to append to the final url.
 *
 * @returns {string} The media url.
 */
/* istanbul ignore next */
function mediaUrlFromTypeV1(type, subCollection = null, itemSlug = null) {
  let url = '';
  /* istanbul ignore next */
  switch (type) {
    case 'konnect':
    case 'early-childhood':
    case 'crosstown':
      url = `/media/${type}`;
      break;
    case 'story':
    case 'stories':
      url = '/media/stories';
      break;
    case 'messages':
      url = `/media/${type}`;
      if (subCollection) {
        url = `/media/${subCollection}`;
      } else if (itemSlug) {
        url = `/media`;
      }
      break;
    case 'leadershippodcast':
    case 'leadership_podcast':
      url = `/leadershippodcast`;
      break;
    case 'lifegroups':
    case 'loop':
    case 'switch':
      url = subCollection
        ? `/media/${type}/${subCollection}`
        : `/media/${type}`;
      break;
    case 'worship':
      url = subCollection ? `/worship/${subCollection}` : `/worship`;
      break;
    default:
      url = '/media/';
  }
  if (itemSlug) {
    url += `/${itemSlug}`;
  }
  return url;
}

/**
 * Convenience function to get the url based on the media type data.
 *
 * @param {object} params - The function params object.
 * @param {string} [params.slug] - The media item name (Default: null).
 * @param {string} [params.subCollection] - The media subCollection name (Default: null).
 * @param {string} [params.type] - The media type. If none specified, results in base /media/ URL.
 *
 * @returns {string} The url for the media type.
 */
function mediaUrlFromTypeV2({ slug = null, subCollection = null, type } = {}) {
  let url = '';
  switch (type) {
    case 'konnect':
    case 'early-childhood':
    case 'crosstown':
      url = `/media/${type}`;
      break;
    case 'stories':
    case 'story':
      url = '/media/stories';
      break;
    case 'messages':
    case 'weekend-messages':
      url = `/media/${type}`;
      if (subCollection) {
        url = `/media/${subCollection}`;
      } else if (slug) {
        url = `/media`;
      }
      break;
    case 'leadershippodcast':
    case 'leadership-podcast':
      url = `/leadershippodcast`;
      break;
    case 'lifegroups':
    case 'loop':
    case 'switch':
      url = subCollection
        ? `/media/${type}/${subCollection}`
        : `/media/${type}`;
      break;
    case 'worship':
      url = subCollection ? `/worship/${subCollection}` : `/worship`;
      break;
    default:
      url = '/media/';
  }
  if (slug) {
    url += `/${slug}`;
  }
  return url;
}

/**
 * @typedef {object} MediaUrlFromTypeParams
 * @property {string} slug - The media item name (Default: null).
 * @property {string} subCollection - The media subCollection name (Default: null).
 * @property {string} type - The media type. If none specified, results in base /media/ URL.
 */

/**
 * Convenience function to get the url based on the media type data.
 *
 * @param {string|MediaUrlFromTypeParams} typeOrParams - The media item name (e.g. 'messages', if string) or params object (if object).
 * @param {string} subCollection - Optional sub-collection value to add to the final url.
 * @param {string} itemSlug - Optional item slug to append to the final url.
 *
 * @returns {string} The url for the media type.
 */
function mediaUrlFromType(typeOrParams, subCollection = null, itemSlug = null) {
  if (typeof typeOrParams === 'string') {
    return mediaUrlFromTypeV1(typeOrParams, subCollection, itemSlug);
  }
  return mediaUrlFromTypeV2(typeOrParams);
}

module.exports = {
  mediaTypeToPath,
  mediaUrlFromType,
};
